<script setup lang="ts">
import { nextTick, ref, defineProps, withDefaults, computed } from "vue";

const props = withDefaults(
  defineProps<{
    containerRef?: HTMLElement | null;
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  }
);

const position = ref<"top" | "bottom">("bottom");
const isOpen = ref(false);
const tooltipRef = ref<HTMLElement | null>(null);
const extraShift = ref(0);

const arrowStyle = computed(() => {
  if (extraShift.value == 0) {
    return "";
  }
  let arrowTransform = extraShift.value * -1;
  if (position.value === "top") {
    arrowTransform -= 8;
  }
  return { transform: "translateX(" + arrowTransform + "px)" };
});
const popperStyle = computed(() => {
  if (extraShift.value == 0) {
    return { transform: "translateX(-50%)" };
  }
  return {
    transform: "translateX(-50%) translateX(" + extraShift.value + "px)",
  };
});

function show() {
  if (props.disabled) {
    return;
  }
  isOpen.value = true;
  nextTick(adjustPosition);
}
function hide() {
  isOpen.value = false;
  extraShift.value = 0;
  position.value = "bottom";
}

function adjustPosition() {
  const rect = tooltipRef.value?.getBoundingClientRect();
  if (!rect) return;
  const boundaryRect = props.containerRef?.getBoundingClientRect();

  // Horizontal check
  let maxRight = window.innerWidth;
  let minRight = 0;
  if (boundaryRect !== undefined) {
    maxRight = boundaryRect.right;
    minRight = boundaryRect.left;
  }

  extraShift.value = 0;
  if (rect.right > maxRight) {
    extraShift.value = maxRight - rect.right;
  }
  if (rect.left < minRight) {
    extraShift.value = -rect.left + 8 + minRight;
  }

  // Vertical check
  const tooltipHeight = rect.height + 16;
  let boundaryTop = window.scrollY;
  let boundaryBottom = window.innerHeight + window.scrollY;
  if (boundaryRect !== undefined) {
    boundaryTop = boundaryRect.top;
    boundaryBottom = boundaryRect.bottom;
  }
  switch (position.value) {
    case "top":
      if (rect.bottom + boundaryTop < tooltipHeight) {
        position.value = "bottom";
      }
      break;
    case "bottom":
      if (boundaryBottom - rect.top < tooltipHeight) {
        position.value = "top";
      }
      break;
  }
}
</script>

<template>
  <div class="relative inline-block">
    <!-- TRIGGER -->
    <div
      @mouseenter="show"
      @mouseleave="hide"
      :class="{ 'cursor-pointer': !disabled }"
    >
      <slot name="trigger" />
    </div>

    <!-- TOOLTIP -->
    <transition
      enter-active-class="transition-opacity ease-out duration-200"
      enter-from-class="opacity-0 translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition-opacity ease-in duration-150"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-1"
    >
      <div
        v-if="isOpen"
        ref="tooltipRef"
        class="absolute left-1/2 -translate-x-1/2 max-md:max-w-80 w-max z-[1000]"
        :class="{
          'flex flex-col items-center': position === 'bottom',
          'bottom-full mb-4': position === 'top',
        }"
        :style="popperStyle"
      >
        <div
          :class="{
            'tooltip-arrow-reverse': position === 'top',
            'tooltip-arrow': position === 'bottom',
          }"
          :style="arrowStyle"
        ></div>
        <div class="rounded-[10px] bg-brown text-white px-3 py-2">
          <slot name="popper" />
        </div>
      </div>
    </transition>
  </div>
</template>

<style lang="scss">
/* Target the custom theme class */
.v-popper--theme-dropdown {
  /* Style the arrow */
  .v-popper__arrow-outer {
    top: 0;
  }
  .v-popper__arrow-inner {
    /* The border color should match the inner background color */
    border-color: #29160e !important; /* Example: red arrow */
  }
}

.tooltip-arrow {
  @apply w-4 h-4 bg-brown [clip-path:polygon(0%_100%,100%_100%,50%_0%)];
}

.tooltip-arrow-reverse {
  @apply w-4 h-4 bg-brown absolute left-1/2 -translate-x-1/2 top-full [clip-path:polygon(50%_100%,0%_0%,100%_0%)];
}
</style>
